// The user-triggered "Reload schema" entry point behind the IntelliSense
// pill's `partial` state (S7 item 5). It does not introspect anything itself:
// it forgets the recorded truncation for `(connectionId, database)` and then
// asks the engine's OWN schema cache for a forced refresh — the same
// `ensure…({ forceRefresh: true })` call the provider's connect-time warm and
// the E2E `__jamSqlEnsureMssqlTables__` seam use. Each cache republishes to
// the schema bus when it completes, which is what flips the pill back (via
// `resolveSchemaReadyStatus` at the provider's emit choke point).
//
// Order matters: clearing BEFORE the refetch is what keeps a re-load honest.
// Only the artefacts that trip on THIS load are re-recorded, so a database
// that no longer exceeds the cap returns the pill to `ready` — and one that
// still exceeds it re-arms `partial` with the current artefact list.
//
// The per-engine `import()` calls are deliberate: the status-bar surface that
// offers this action must not pull all five schema-cache modules into its own
// chunk just to keep a button enabled.

import type { DatabaseEngineId } from '@/core/engines';

import { publishedDatabases } from '@/features/intellisense/schemaBus/schemaBus';

import { clearTruncation } from './introspectionTruncation';

export type ReloadSchemaSnapshotParams = {
  connectionId: string;
  database: string;
  engine: DatabaseEngineId;
};

/**
 * Engines `reloadSchemaSnapshot` can actually refresh. Kusto counts: its
 * schema lives in the monaco-kusto worker rather than a renderer cache, and
 * the provider re-reads it per registered document. Only the mock engine
 * has nothing to reload.
 */
export function supportsIntelliSenseRefresh(engine: DatabaseEngineId): boolean {
  return engine === 'mssql' || engine === 'pgsql' || engine === 'mysql'
    || engine === 'oracle' || engine === 'sqlite' || engine === 'kusto';
}

/**
 * Databases of `connectionId` whose IntelliSense schema is loaded RIGHT NOW —
 * the only ones a manual refresh from the Object Explorer may re-read.
 *
 * Engine-aware on purpose: MSSQL's cache is per database, so every published
 * bus snapshot counts; PostgreSQL / MySQL / SQLite / Oracle hold ONE snapshot
 * per connection, so only the database (Oracle: schema) that snapshot is for
 * counts — the bus may still carry an older database's entry, and a forced
 * load of it would evict the snapshot the open tabs read; Kusto keeps no
 * cache at all, so the answer comes from the documents its provider has a
 * schema loaded for. Same dynamic imports as `reloadSchemaSnapshot`, for the
 * same chunk reason.
 */
export async function loadedIntelliSenseDatabases(
  connectionId: string,
  engine: DatabaseEngineId,
): Promise<string[]> {
  if (!connectionId) return [];
  switch (engine) {
    case 'mssql':
      return publishedDatabases(connectionId);
    case 'pgsql': {
      const { getCachedPgSchema } = await import('./pgSchemaCache');
      const database = getCachedPgSchema(connectionId)?.database;
      return database ? [database] : [];
    }
    case 'mysql': {
      const { getCachedMysqlSchema } = await import('./mysqlSchemaCache');
      const database = getCachedMysqlSchema(connectionId)?.database;
      return database ? [database] : [];
    }
    case 'oracle': {
      const { getCachedOracleSchema } = await import('./oracleSchemaCache');
      const schema = getCachedOracleSchema(connectionId)?.schema;
      return schema ? [schema] : [];
    }
    case 'sqlite': {
      const { getCachedSqliteSchema } = await import('./sqliteSchemaCache');
      const database = getCachedSqliteSchema(connectionId)?.database;
      return database ? [database] : [];
    }
    case 'kusto': {
      const { loadedKustoDatabases } = await import('./kustoSchemaLoad');
      return loadedKustoDatabases(connectionId);
    }
    default:
      return [];
  }
}

/**
 * Force-refresh the IntelliSense schema snapshot for `(connectionId,
 * database)`. Resolves `true` when a refresh ran, `false` when there was
 * nothing to reload — the mock engine, or Kusto with no document open on
 * the pair.
 * Rejects with whatever the underlying cache rejects with, so the caller can
 * put the pill into `error` rather than silently claiming success.
 */
export async function reloadSchemaSnapshot(
  params: ReloadSchemaSnapshotParams,
): Promise<boolean> {
  const { connectionId, database, engine } = params;
  if (!connectionId || !database) return false;

  clearTruncation(connectionId, database);

  switch (engine) {
    case 'mssql': {
      // `…Complete` (not the plain `ensureMssqlTables`) because every engine's
      // load is tiered: the plain call resolves on the phase-1 relations
      // publish, while columns / indexes / FKs / routines — the artefacts most
      // likely to trip a cap — only land in phase 2. Resolving early would let
      // the caller read a not-yet-final truncation record and flash the pill
      // green before phase 2 turned it amber again.
      const { ensureMssqlTablesComplete } = await import('./mssqlTableCache');
      await ensureMssqlTablesComplete({ connectionId, database, forceRefresh: true });
      return true;
    }
    // Every engine below is tiered too (S7 item 8), so they all take the
    // `…Complete` variant for the same reason mssql does.
    case 'pgsql': {
      const { ensurePgSchemaComplete } = await import('./pgSchemaCache');
      await ensurePgSchemaComplete({ connectionId, database, forceRefresh: true });
      return true;
    }
    case 'mysql': {
      const { ensureMysqlSchemaComplete } = await import('./mysqlSchemaCache');
      await ensureMysqlSchemaComplete({ connectionId, database, forceRefresh: true });
      return true;
    }
    case 'oracle': {
      const { ensureOracleSchemaComplete } = await import('./oracleSchemaCache');
      // Oracle's cache is keyed by connection and scoped by SCHEMA — the
      // renderer's "database" for an Oracle connection IS the schema name
      // (same mapping `oracleLanguageServiceProvider` and the resolver's
      // pull adapter use).
      await ensureOracleSchemaComplete({ connectionId, schema: database, forceRefresh: true });
      return true;
    }
    case 'sqlite': {
      const { ensureSqliteSchemaComplete } = await import('./sqliteSchemaCache');
      await ensureSqliteSchemaComplete({ connectionId, database, forceRefresh: true });
      return true;
    }
    case 'kusto': {
      // No renderer cache to force: the provider re-runs `.show schema` into
      // the monaco-kusto worker for every document open on the pair — `false`
      // when no document is open on it (nothing was reloaded).
      const { reloadKustoSchema } = await import('./kustoSchemaLoad');
      return reloadKustoSchema(connectionId, database);
    }
    default:
      return false;
  }
}
